export const METHODOLOGY_VERSION = "rug-lens/0.4.0";

export type FindingStatus = "observed" | "not_observed" | "unknown" | "not_assessed";
export type ImpactClass =
  | "limited"
  | "material"
  | "near_total"
  | "arbitrary_rules"
  | "not_estimated";

export type CapabilityId =
  | "supply_dilution"
  | "holding_control"
  | "transfer_restriction"
  | "fee_modification"
  | "liquidity_withdrawal"
  | "upgrade_or_arbitrary_behavior"
  | "other_programmable";

export type AssuranceLevel =
  | "pool_estimate"
  | "aggregator_quote"
  | "route_simulated"
  | "synthetic_simulated"
  | "holder_context_simulated"
  | "unknown";

export type ExitState =
  | "quoted"
  | "simulated_success"
  | "simulation_failed"
  | "holder_context_unavailable"
  | "poor_proceeds"
  | "no_supported_route"
  | "insufficient_liquidity"
  | "provider_unavailable"
  | "provider_rate_limited"
  | "unsupported"
  | "unknown";

export interface ObservationContext {
  observedAt: string;
  slot: number | null;
  blockTime: string | null;
  commitment: "finalized" | "confirmed" | "fixture" | "unknown";
  provider: string;
  replayable: boolean;
}

export interface EvidenceItem {
  id: string;
  label: string;
  address: string | null;
  field: string;
  rawValue: unknown;
  interpretation: string;
  sourceUrl: string | null;
  context: ObservationContext;
}

export interface ControlConstraint {
  controller: string | null;
  kind:
    | "none"
    | "wallet_shaped"
    | "token_multisig"
    | "program_controlled"
    | "unresolved"
    | "fixture";
  controllingProgram: string | null;
  minimumCoalition: string;
  warningDelay: string;
  notes: string;
}

export interface LiquidityCoalitionThreshold {
  targetPercentage: 25 | 50 | 80;
  minimumControllerAddresses: number | null;
  accumulatedSharePercentage: number | null;
}

export type LiquidityControllerKind =
  | "wallet_shaped"
  | "token_multisig"
  | "program_controlled"
  | "unresolved"
  | "provably_unspendable"
  | "fixture";

export interface LiquidityControllerExposure {
  rank: number;
  authority: string | null;
  authorityKind: LiquidityControllerKind;
  controllingProgram: string | null;
  shareOfWithdrawalBasePercentage: number;
  directlySpendable: boolean;
  interpretation: string;
  sourceUrl: string | null;
}

export interface LiquidityControlAnalysis {
  scope: "deepest_eligible_reference_pool";
  protocol: string | null;
  poolType: string | null;
  poolAddress: string | null;
  poolUrl: string | null;
  liquidityUsd: number | null;
  lpMint: string | null;
  poolWithdrawalBaseSupplyAtomic: string | null;
  currentLpMintSupplyAtomic: string | null;
  poolCreatorAddress: string | null;
  withdrawalsCurrentlyEnabled: boolean | null;
  topAccountsAnalyzed: number;
  topAccountsCoveragePercentage: number | null;
  effectiveClaimCoveragePercentage: number | null;
  burnedLpSharePercentage: number | null;
  largestDirectControllerSharePercentage: number | null;
  directlySpendableSharePercentage: number | null;
  programControlledSharePercentage: number | null;
  unresolvedSharePercentage: number | null;
  opaqueLiveClaimPercentage: number | null;
  provablyUnspendableSharePercentage: number | null;
  outsideTopAccountsPercentage: number | null;
  coalitionThresholds: LiquidityCoalitionThreshold[];
  controllers: LiquidityControllerExposure[];
  creatorRelationship: "not_checked";
}

export interface CapabilityFinding {
  id: CapabilityId;
  title: string;
  plainQuestion: string;
  status: FindingStatus;
  summary: string;
  whyItMatters: string;
  mechanisms: string[];
  estimatedExposure: string;
  impactClass: ImpactClass;
  control: ControlConstraint;
  preconditions: string[];
  coverage: string;
  evidence: EvidenceItem[];
  liquidityControl?: LiquidityControlAnalysis;
}

export interface TokenIdentity {
  chain: "solana";
  network: string;
  networkIdentity: string;
  address: string;
  name: string | null;
  symbol: string | null;
  decimals: number;
  tokenProgram: string;
  identityStatus:
    | "registry_recognized"
    | "unverified"
    | "unknown"
    | "fixture";
  assetType:
    | "protocol_receipt"
    | "governance"
    | "community_token"
    | "stable_or_wrapped"
    | "unknown";
  metadataWarning: string;
}

export type IdentityStatus = "registry_recognized" | "unverified" | "unknown";

export interface IdentitySource {
  provider: "jupiter" | "coingecko" | "fixture";
  label: string;
  url: string | null;
  recognition: "verified" | "listed" | "synthetic_match";
}

export interface IdentityReport {
  status: IdentityStatus;
  label: string;
  headline: string;
  plainSummary: string[];
  recognizedAddress: string | null;
  sources: IdentitySource[];
  matchedFields: string[];
  coverage: string[];
  limitations: string[];
  observedAt: string;
  methodologyVersion: string;
}

export interface RugCapacityReport {
  headline: string;
  plainSummary: string[];
  findings: CapabilityFinding[];
  coverage: string[];
  limitations: string[];
  observationRange: {
    fromSlot: number | null;
    toSlot: number | null;
    mixedContext: boolean;
    observedAt: string;
  };
  methodologyVersion: string;
}

export interface MarketReference {
  status: "available" | "unavailable";
  priceUsd: number | null;
  source: string;
  pairAddress: string | null;
  pairUrl: string | null;
  liquidityUsd: number | null;
  observedAt: string;
  confidenceNote: string;
}

export interface ExitLadderRow {
  id: string;
  requestedUsd: number | null;
  inputTokenAmount: string;
  inputAtomicAmount: string;
  referenceInputValueUsd: number | null;
  outputAsset: string;
  outputAssetAddress: string;
  expectedGrossOutput: number | null;
  expectedOutputAssetDelta: number | null;
  networkCostNative: number | null;
  allInNetValueUsd: number | null;
  estimatedRecoveryPercentage: number | null;
  priceImpactPercentage: number | null;
  poolAndRouteFees: string;
  tokenOrTransferFees: string;
  route: string[];
  quoteAgeSeconds: number | null;
  assurance: AssuranceLevel;
  simulationContext: string;
  simulationResult: "not_run" | "succeeded" | "failed" | "unknown";
  state: ExitState;
  failureReason: string | null;
  context: ObservationContext;
}

export interface ExecutableExitReport {
  headline: string;
  plainSummary: string[];
  exitAsset: {
    symbol: string;
    address: string;
    trustReason: string;
  };
  reference: MarketReference;
  rows: ExitLadderRow[];
  simulationNote: string;
  directPoolCheck: string;
  limitations: string[];
  methodologyVersion: string;
}

export type HolderAuthorityKind =
  | "wallet_shaped"
  | "token_multisig"
  | "program_controlled"
  | "unresolved"
  | "fixture";

export type DumpRiskBand = "high" | "elevated" | "low_observed" | "unknown";

export interface HolderExposure {
  rank: number;
  tokenAccounts: string[];
  authority: string | null;
  authorityKind: HolderAuthorityKind;
  controllingProgram: string | null;
  tokenAmount: string;
  shareOfSupplyPercentage: number;
  interpretation: string;
  sourceUrl: string | null;
}

export interface DumpCapacityReport {
  status: FindingStatus;
  riskBand: DumpRiskBand;
  headline: string;
  plainSummary: string[];
  topAccountsAnalyzed: number;
  topAccountsSharePercentage: number;
  walletShapedSharePercentage: number;
  programControlledSharePercentage: number;
  unresolvedSharePercentage: number;
  largestWalletShapedSharePercentage: number | null;
  holders: HolderExposure[];
  coverage: string[];
  limitations: string[];
  observationRange: {
    fromSlot: number | null;
    toSlot: number | null;
    mixedContext: boolean;
    observedAt: string;
  };
  methodologyVersion: string;
}

export interface AnalysisBundle {
  mode: "fixture" | "live";
  fixtureId: string | null;
  fixtureLabel: string | null;
  identity: TokenIdentity;
  identityReport: IdentityReport;
  rugCapacity: RugCapacityReport;
  dumpCapacity: DumpCapacityReport;
  executableExit: ExecutableExitReport;
  generatedAt: string;
}

export interface FixtureSummary {
  id: string;
  title: string;
  eyebrow: string;
  description: string;
  lesson: string;
  tone: "calm" | "warning" | "danger" | "unknown";
}

export interface ProviderState {
  configured: boolean;
  mode: "managed" | "session" | "public" | "keyless" | "unavailable";
  label: string;
  detail: string;
}

export interface ProviderStatus {
  localByokEnabled: boolean;
  solanaRpc: ProviderState;
  jupiter: ProviderState;
  coinGecko: ProviderState;
  dexScreener: ProviderState;
  sessionHasHeliusKey: boolean;
  sessionHasJupiterKey: boolean;
}

export const CAPABILITY_DEFINITIONS: Record<
  CapabilityId,
  { title: string; plainQuestion: string; whyItMatters: string }
> = {
  supply_dilution: {
    title: "Supply dilution",
    plainQuestion: "Can someone create more tokens?",
    whyItMatters:
      "New supply can be sold into the market and reduce the value represented by each existing token.",
  },
  holding_control: {
    title: "Freeze or take holdings",
    plainQuestion: "Can someone freeze, burn, or move a holder's tokens?",
    whyItMatters:
      "A holder may lose control of their balance even when they never approve a transfer.",
  },
  transfer_restriction: {
    title: "Transfer restrictions",
    plainQuestion: "Can transfers or sells be stopped or selectively allowed?",
    whyItMatters:
      "A token can look tradable while rules prevent particular wallets or routes from exiting.",
  },
  fee_modification: {
    title: "Fee or tax changes",
    plainQuestion: "Can someone raise transfer fees or change economic rules?",
    whyItMatters:
      "A controller may be able to make selling uneconomic even if today's fee is small.",
  },
  liquidity_withdrawal: {
    title: "Liquidity withdrawal",
    plainQuestion: "Can one on-chain controller pull a large share of usable liquidity?",
    whyItMatters:
      "Displayed liquidity can disappear if one authority directly controls a large share of the relevant LP tokens or positions.",
  },
  upgrade_or_arbitrary_behavior: {
    title: "Upgrade or arbitrary rules",
    plainQuestion: "Can someone replace code that decides how the token behaves?",
    whyItMatters:
      "An upgrade path can introduce new restrictions or holder powers after the report is created.",
  },
  other_programmable: {
    title: "Other programmable behavior",
    plainQuestion: "Did the analyzer find mechanisms it cannot safely classify?",
    whyItMatters:
      "Unknown custom behavior should reduce confidence instead of being mistaken for safety.",
  },
};

export function summarizeFindings(findings: CapabilityFinding[]): string {
  const observed = findings.filter((finding) => finding.status === "observed");
  const unknown = findings.filter((finding) => finding.status === "unknown");
  const notAssessed = findings.filter((finding) => finding.status === "not_assessed");

  if (observed.length === 0 && unknown.length === 0 && notAssessed.length === 0) {
    return "No material controller capability was observed within the checks this version supports.";
  }

  const observedText =
    observed.length === 0
      ? "No material capability was observed"
      : `${observed.length} material controller ${observed.length === 1 ? "capability was" : "capabilities were"} observed`;
  const unknownText =
    unknown.length === 0
      ? ""
      : `; ${unknown.length} ${unknown.length === 1 ? "area remains" : "areas remain"} unknown`;
  const notAssessedText =
    notAssessed.length === 0
      ? ""
      : `; ${notAssessed.length} ${notAssessed.length === 1 ? "area is" : "areas are"} outside this version's decoder`;

  return `${observedText}${unknownText}${notAssessedText}.`;
}

export function shortAddress(address: string, head = 5, tail = 5): string {
  if (address.length <= head + tail + 3) return address;
  return `${address.slice(0, head)}…${address.slice(-tail)}`;
}

export function impactLabel(impact: ImpactClass): string {
  const labels: Record<ImpactClass, string> = {
    limited: "Limited",
    material: "Material",
    near_total: "Near-total",
    arbitrary_rules: "Arbitrary rules",
    not_estimated: "Not estimated",
  };
  return labels[impact];
}

export function statusLabel(status: FindingStatus): string {
  if (status === "observed") return "Observed";
  if (status === "not_observed") return "Not observed in coverage";
  if (status === "not_assessed") return "Not assessed in this version";
  return "Unknown";
}

export function assuranceLabel(value: AssuranceLevel): string {
  const labels: Record<AssuranceLevel, string> = {
    pool_estimate: "Pool estimate",
    aggregator_quote: "Aggregator quote",
    route_simulated: "Route simulated",
    synthetic_simulated: "Synthetic simulation",
    holder_context_simulated: "Holder-context simulation",
    unknown: "Unknown",
  };
  return labels[value];
}
